/**
 * ReportEmbed — rendu LIVE nu d'un rapport (première page uniquement), sans
 * chrome de l'app (ni header, ni nav, ni sidebar). Destiné à être iframé et
 * mis à l'échelle par la liste des rapports pour servir de miniature.
 *
 * Rend à sa taille NATIVE (1100 px de large) : le scaling est fait par le
 * conteneur parent (l'iframe côté carte), pas ici.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import PageCanvas from '../features/reports/PageCanvas'
import { getTheme } from '../features/reportThemes/reportThemesApi'
import { useApi } from '../hooks/useApi'
import type { Layout, PeriodValue } from '../features/reports/schemas'
import type { ReportTheme } from '../types/reportTheme'

interface ReportPageFull {
  id: number
  position: number
  name: string
  layout: Layout
}

interface ReportFull {
  id: number
  name: string
  themeId?: number | null
  defaultPeriod?: unknown
  pages: ReportPageFull[]
}

/** 30 jours finissant HIER (convention GA4, recopié de ReportBuilder). */
function defaultPeriod(): PeriodValue {
  const end = new Date()
  end.setDate(end.getDate() - 1)
  const start = new Date()
  start.setDate(start.getDate() - 30)
  const toIso = (d: Date) => d.toISOString().slice(0, 10)
  return { start: toIso(start), end: toIso(end), mode: 'duration', compare: null }
}

/** Un defaultPeriod stocké n'est exploitable que s'il a des dates concrètes
 *  (les anciens rapports ont la forme `{mode, range}` sans start/end). */
function toPeriodValue(stored: unknown): PeriodValue {
  if (
    stored && typeof stored === 'object' &&
    typeof (stored as any).start === 'string' && (stored as any).start &&
    typeof (stored as any).end === 'string' && (stored as any).end
  ) {
    const s = stored as any
    return {
      start: s.start,
      end: s.end,
      mode: s.mode === 'previous_month' ? 'previous_month' : 'duration',
      compare: s.compare ?? null,
    }
  }
  return defaultPeriod()
}

/** Bloc blanc à la taille native du canevas — évite tout flash pendant le chargement
 *  et sert de fallback pour un rapport vide / en erreur (c'est une miniature). */
function BlankCanvas() {
  return <div style={{ width: 1100, height: 1556, background: '#fff' }} />
}

export default function ReportEmbed() {
  const { projectId, reportId } = useParams<{ projectId: string; reportId: string }>()
  const { data: report, loading, error } = useApi<ReportFull>(
    `/api/reporting/clients/${projectId}/reports/${reportId}`,
  )

  const [theme, setTheme] = useState<ReportTheme | null>(null)

  useEffect(() => {
    let cancelled = false
    if (report?.themeId) {
      getTheme(report.themeId)
        .then((t) => {
          if (!cancelled) setTheme(t)
        })
        .catch(() => {
          if (!cancelled) setTheme(null)
        })
    } else {
      setTheme(null)
    }
    return () => {
      cancelled = true
    }
  }, [report?.themeId])

  const wrapper = { width: 1100, background: '#fff' } as const

  if (loading) {
    return (
      <div style={wrapper}>
        <BlankCanvas />
      </div>
    )
  }

  const page0 = report?.pages
    ? [...report.pages].sort((a, b) => a.position - b.position)[0]
    : undefined

  if (error || !report || !page0) {
    return (
      <div style={wrapper}>
        <BlankCanvas />
      </div>
    )
  }

  const period = toPeriodValue(report.defaultPeriod)

  return (
    <div style={wrapper}>
      <PageCanvas
        layout={page0.layout}
        mode="preview"
        period={period}
        theme={theme}
        selectedWidgetId={null}
        draggingType={null}
        onLayoutChange={() => {}}
        onSelectWidget={() => {}}
        onEditWidget={() => {}}
        onWidgetContentChange={() => {}}
        onWidgetViewChange={() => {}}
      />
    </div>
  )
}
